import React from "react";
import { Modal, Button, Form, InputGroup, FormControl } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ColorPicker from "../../../lib/plugins/color-picker/ColorPicker";
import { getBadgeSettings, getBadgeBoxStyle, resolveBadgeColors } from "../../../lib/global/helpers";

const SHAPE_FIELDS = [
  { field: "paddingX", label: "Padding (left/right)" },
  { field: "paddingY", label: "Padding (top/bottom)" },
  { field: "borderRadius", label: "Corner Radius" },
  { field: "borderWidth", label: "Border Width" },
];

const RULE_COLOURS = [
  { field: "background", label: "Background" },
  { field: "borderColor", label: "Border" },
  { field: "color", label: "Text" },
];

// "Badge & colour rules" dialog for one text element. Reads/writes the
// element's `badge` (see getBadgeSettings in helpers.js) through
// `onChange(nextBadge)`. `fontColor` is the element's own font colour, used
// for the text when no badge colour is set.
export default function BadgeSettings({ show, element, fontColor, onChange, onHide }) {
  const settings = getBadgeSettings(element);
  const update = (patch) => onChange({ ...(element?.badge || {}), ...settings, ...patch });
  const updateDefault = (patch) => update({ default: { ...settings.default, ...patch } });
  const updateRule = (index, patch) =>
    update({ rules: settings.rules.map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  const removeRule = (index) => update({ rules: settings.rules.filter((_, i) => i !== index) });
  const moveRule = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= settings.rules.length) return;
    const rules = [...settings.rules];
    [rules[index], rules[target]] = [rules[target], rules[index]];
    update({ rules });
  };
  const addRule = () =>
    update({ rules: [...settings.rules, { value: "", background: "", borderColor: "", color: "" }] });

  // How `text` prints with the current settings.
  const renderSample = (text) => {
    const colors = resolveBadgeColors(element, text);
    const box = getBadgeBoxStyle(element, text);
    const color = colors?.color || fontColor;
    return (
      <span style={{ fontSize: "10pt", color }}>
        {box ? <span style={{ ...box, color }}>{text}</span> : text}
      </span>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      scrollable
      size="lg"
      dialogClassName="badge-settings-modal"
      backdropClassName="preview-modal-backdrop"
      style={{ zIndex: 10700 }}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">Badge &amp; colour rules</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="forBadgeEnabled"
            checked={settings.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="forBadgeEnabled">
            Show as a badge (box behind the text)
          </label>
        </div>

        {settings.enabled && (
          <>
            <div className="d-flex flex-wrap mb-3" style={{ gap: "12px" }}>
              {SHAPE_FIELDS.map(({ field, label }) => (
                <Form.Group key={field} style={{ width: "150px" }}>
                  <Form.Label className="mb-1">{label}</Form.Label>
                  <InputGroup size="sm" style={{ flexWrap: "nowrap" }}>
                    <FormControl
                      type="number"
                      min="0"
                      value={settings[field]}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        update({ [field]: value >= 0 ? value : 0 });
                      }}
                    />
                    <InputGroup.Text>px</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              ))}
            </div>

            <Form.Label className="fw-bold mb-1">Default colours</Form.Label>
            <Form.Text className="text-muted d-block mb-2">
              Used when the printed value doesn't match any rule below.
            </Form.Text>
            <div className="d-flex flex-wrap align-items-end mb-3" style={{ gap: "12px" }}>
              {RULE_COLOURS.map(({ field, label }) => (
                <Form.Group key={field} className="compact-colour-field" style={{ width: "170px" }}>
                  <Form.Label className="mb-1">{label}</Form.Label>
                  <ColorPicker
                    color={settings.default[field] || (field === "color" ? fontColor : "#FFFFFFFF")}
                    callbackFromParents={(e) => updateDefault({ [field]: e.hex })}
                  />
                </Form.Group>
              ))}
              {settings.default.color && (
                <Button variant="link" size="sm" className="p-0 mb-1" onClick={() => updateDefault({ color: "" })}>
                  Use font colour for text
                </Button>
              )}
            </div>
          </>
        )}

        <hr />
        <Form.Label className="fw-bold mb-1">Colour rules</Form.Label>
        <Form.Text className="text-muted d-block mb-2">
          When the printed value matches a rule (ignoring upper/lower case and spaces at the ends), its
          colours are used. The first matching rule wins. Empty colours fall back to the defaults.
          {!settings.enabled && " With the badge off, rules only change the text colour."}
        </Form.Text>

        {settings.rules.length === 0 && (
          <div className="text-muted small mb-2">No rules yet.</div>
        )}

        {settings.rules.map((rule, index) => (
          <div key={index} className="d-flex flex-wrap align-items-end border rounded p-2 mb-2" style={{ gap: "10px" }}>
            <Form.Group style={{ width: "160px" }}>
              <Form.Label className="mb-1">When value is</Form.Label>
              <FormControl
                size="sm"
                type="text"
                placeholder="e.g. Confirmed"
                value={rule.value || ""}
                onChange={(e) => updateRule(index, { value: e.target.value })}
              />
            </Form.Group>
            {RULE_COLOURS.filter(({ field }) => settings.enabled || field === "color").map(({ field, label }) => (
              <Form.Group key={field} className="compact-colour-field" style={{ width: "150px" }}>
                <Form.Label className="mb-1">{label}</Form.Label>
                <ColorPicker
                  color={rule[field] || settings.default[field] || (field === "color" ? fontColor : "#FFFFFFFF")}
                  callbackFromParents={(e) => updateRule(index, { [field]: e.hex })}
                />
              </Form.Group>
            ))}
            <div className="mb-1" style={{ minWidth: "90px" }}>
              {rule.value ? renderSample(rule.value) : <span className="text-muted small">—</span>}
            </div>
            <div className="ms-auto mb-1 d-flex" style={{ gap: "4px" }}>
              <Button size="sm" variant="outline-secondary" title="Move up" disabled={index === 0} onClick={() => moveRule(index, -1)}>
                <FontAwesomeIcon icon="arrow-up" />
              </Button>
              <Button size="sm" variant="outline-secondary" title="Move down" disabled={index === settings.rules.length - 1} onClick={() => moveRule(index, 1)}>
                <FontAwesomeIcon icon="arrow-down" />
              </Button>
              <Button size="sm" variant="outline-danger" title="Remove rule" onClick={() => removeRule(index)}>
                <FontAwesomeIcon icon="trash" />
              </Button>
            </div>
          </div>
        ))}

        <Button size="sm" variant="outline-primary" onClick={addRule}>
          <FontAwesomeIcon icon="plus" className="me-1" />
          Add rule
        </Button>

        {settings.enabled && (
          <div className="mt-3">
            <small className="text-muted me-2">Any other value:</small>
            {renderSample("Other value")}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
